import { useState } from 'react';

import { RightOutlined } from '@ant-design/icons';
import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { AntColumnType } from '@revenuemarket/ui/antd/Table';
import { formatBusinessNumber } from '@revenuemarket/utils';
import { Table, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import { InvoiceMapping } from '~apis/mapping';
import { TAX_INVOICE_TYPE } from '~apis/tax-invoice';
import SettlementTaxInvoiceResultDrawer from '~components/BusinessEntity/SettlementTaxInvoiceResultDrawer';
import { useGetInvoiceMappings } from '~hooks/queries/mapping/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { TaxInvoiceType } from '~models/tax-invoice';

import MappingAmountInfoRenderer from './MappingAmountInfoRenderer';
import MappingTaxInvoiceBtn from './MappingTaxInvoiceButton';

export const headerStyle = {
  background: 'white',
  color: colorTokens.textDescription,
  borderTop: `1px solid ${colorTokens.borderDefault}`,
};

interface Props {
  startDate: Dayjs;
  endDate: Dayjs;
  type: TAX_INVOICE_TYPE;
  searchParam?: string;
  page: number;
  setPage: (page: number) => void;
}

const COLUMNS = ({
  showYear,
  type,
}: {
  showYear: boolean;
  type: TaxInvoiceType;
}): AntColumnType<InvoiceMapping> => {
  return [
    {
      title: '발급일자',
      width: 120,
      dataIndex: 'reportingDate',
      key: 'reportingDate',
      render: (value) =>
        value ? (
          showYear ? (
            <Flex direction="column">
              <Typography.Text
                style={{
                  color: colorTokens.textDescription,
                }}
              >
                {dayjs(value).format('YYYY년')}
              </Typography.Text>
              <Typography.Text
                style={{
                  color: colorTokens.textDescription,
                }}
              >
                {dayjs(value).format('M월 D일')}
              </Typography.Text>
            </Flex>
          ) : (
            <Typography.Text
              style={{
                color: colorTokens.textDescription,
              }}
            >
              {dayjs(value).format('M월 D일')}
            </Typography.Text>
          )
        ) : (
          '-'
        ),
    },
    {
      title: '거래처',
      dataIndex: 'counterPartInfo',
      key: 'counterPartInfo',
      width: 180,
      render: (value, record) => {
        return (
          <Flex direction="column">
            <Typography.Text
              style={{
                fontSize: 14,
              }}
            >
              {record.counterPartyCompanyName}
            </Typography.Text>
            <Typography.Text
              style={{
                color: colorTokens.textDescription,
              }}
            >
              {`${formatBusinessNumber(record.counterPartyBusinessRegNumber)} ${
                record.counterPartyName ? `| ${record.counterPartyName}` : ``
              }`}
            </Typography.Text>
          </Flex>
        );
      },
    },
    {
      title: '대표 품목',
      dataIndex: 'repItems',
      key: 'repItems',
      width: 180,
      render: (value, record) => {
        return (
          <Flex align="center" gap={4}>
            <Typography.Text
              ellipsis
              style={{
                cursor: 'pointer',
                fontSize: 14,
                color: !value ? colorTokens.textDescription : colorTokens.textDefault,
              }}
              onClick={() => {
                overlays.open(({ isOpen, onClose }) => (
                  <SettlementTaxInvoiceResultDrawer
                    onClose={onClose}
                    isOpen={isOpen}
                    type={record.taxInvoiceType}
                    id={record.taxInvoiceId}
                  />
                ));
              }}
            >
              {value || '품목 정보 없음'}
            </Typography.Text>
            <RightOutlined
              style={{
                color: colorTokens.textDescription,
              }}
            />
          </Flex>
        );
      },
    },
    {
      title: '미정산 잔액 / 합계 금액',
      dataIndex: 'amountInfo',
      key: 'amountInfo',
      align: 'right',
      width: 150,
      render: (_, record) => {
        return (
          <MappingAmountInfoRenderer
            unmappedAmount={record.unmappedAmount}
            totalAmount={record.totalAmount}
            taxInvoiceType={record.taxInvoiceType}
            taxInvoiceId={record.taxInvoiceId}
          />
        );
      },
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'right',
      width: 220,
      render: (value, record) => {
        return (
          <MappingTaxInvoiceBtn item={record} type={type} taxInvoiceId={record.taxInvoiceId} />
        );
      },
    },
  ];
};

const MappingTaxInvoiceListTable: React.FC<Props> = ({
  startDate,
  endDate,
  type,
  searchParam,
  page,
  setPage,
}) => {
  const [size, setSize] = useState(20);

  const { companyId } = useDemoAwareCompanyId();
  const { data, isLoading } = useGetInvoiceMappings({
    companyId,
    requestParams: {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      type,
      page,
      size,
      searchParam,
    },
  });

  const totalElements = data?.Data?.data?.totalElements || 0;
  const taxInvoiceList = data?.Data?.data?.content || [];
  const showYear = startDate.year() !== endDate.year();
  return (
    <Table
      scroll={{
        x: '50vw',
        y: '50vh',
      }}
      columns={COLUMNS({ showYear, type }).map((column) => ({
        ...column,
        onHeaderCell: () => ({
          style: headerStyle,
        }),
      }))}
      dataSource={taxInvoiceList}
      loading={isLoading}
      pagination={{
        onChange: (page) => {
          setPage(page - 1);
        },
        pageSize: size,
        current: page + 1,
        total: totalElements,
        showSizeChanger: true,
        onShowSizeChange(current, size) {
          setSize(size);
        },
      }}
    />
  );
};

export default MappingTaxInvoiceListTable;
